import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import TransferPrintView, { TransferPrintRow } from './TransferPrintView';
import { Fact, TRANSFER_STATUS, Translate, documentDateTime } from '../../components/purchasing';

export const personName = (user: any): string | null => {
  const name = [user?.firstName, user?.lastName].filter((part) => part && String(part).trim()).join(' ').trim();
  return name || null;
};

/** The header facts of a transfer: the route, why, then the dates in the order they happen. */
export const transferFacts = (transfer: any, t: Translate): Fact[] => {
  if (!transfer) return [];
  const statusId = Number(transfer.status_id);
  const sent = [TRANSFER_STATUS.Sent, TRANSFER_STATUS.Received, TRANSFER_STATUS.Rejected, TRANSFER_STATUS.Cancelled].includes(statusId as any) && transfer.sent_date;
  let received: Pick<Fact, 'value' | 'pending' | 'lines'>;
  if (statusId === TRANSFER_STATUS.Received) received = { value: documentDateTime(transfer.received_date), lines: [personName(transfer.receivedBy)].filter(Boolean) as string[] };
  else if (statusId === TRANSFER_STATUS.Rejected)
    received = { value: t('Rejected'), lines: [personName(transfer.receivedBy), transfer.received_date ? documentDateTime(transfer.received_date) : null].filter(Boolean) as string[] };
  else if (statusId === TRANSFER_STATUS.Cancelled) received = { pending: t('Cancelled') };
  else received = { pending: t('Not received yet') };
  return [
    { key: 'from', label: t('From'), value: transfer.fromLocation?.name, pending: '—' },
    { key: 'to', label: t('To'), value: transfer.toLocation?.name, pending: '—' },
    { key: 'reason', label: t('Reason'), value: transfer.reason?.reasonName, pending: t('No reason') },
    { key: 'created', label: t('Created'), value: documentDateTime(transfer.created_at), lines: [personName(transfer.createdByUser)].filter(Boolean) as string[], newRow: true, numeric: true },
    {
      key: 'sent',
      label: t('Sent'),
      numeric: true,
      value: sent ? documentDateTime(transfer.sent_date) : undefined,
      pending: t('Not sent yet'),
      lines: sent ? ([personName(transfer.sentBy)].filter(Boolean) as string[]) : [],
    },
    { key: 'received', label: t('Received'), numeric: true, ...received },
  ];
};

const docDate = (value?: string | null) => {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '–';
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
};

/** The stock-transfer document: rendered off screen, shown by Print and captured by Download PDF. */
export const useTransferPrint = (transfer: any, rows: TransferPrintRow[]) => {
  // Document language follows the site slug language (same as every other function); no manual chooser.
  const printLang: 'al' | 'en' = localStorage.getItem('language') === 'en' ? 'en' : 'al';
  const ref = useRef<HTMLDivElement>(null);
  const company = transfer?.company ?? null;

  const download = () => {
    const node = ref.current;
    if (!node) return;
    // html2pdf yields a blank canvas when .from() gets the position:absolute wrapper; capture the in-flow node inside it.
    const target = (node.firstElementChild as HTMLElement) ?? node;
    html2pdf()
      .set({
        margin: 8,
        filename: `stock-transfer-${transfer?.internal_number ?? ''}-${printLang}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(target)
      .save();
  };

  const node = (
    <>
      {/* During window.print() show only the document. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .transfer-print-content, .transfer-print-content * { visibility: visible; }
          .transfer-print-content { position: absolute !important; left: 0 !important; top: 0 !important; width: 100% !important; background: #fff; }
          .transfer-print-content > div { width: 100% !important; }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="transfer-print-content" ref={ref} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <TransferPrintView
          lang={printLang}
          received={Number(transfer?.status_id) === TRANSFER_STATUS.Received}
          company={{
            companyName: company?.companyName ?? '',
            companyNumber: company?.companyNumber ?? '',
            addressLine1: company?.addressLine1 ?? '',
            addressLine2: company?.addressLine2 ?? '',
            city: company?.city ?? '',
            postalCode: company?.postalCode ?? '',
          }}
          transferNumber={transfer?.internal_number}
          date={docDate(transfer?.sent_date ?? transfer?.created_at)}
          statusId={transfer?.status_id}
          fromLocation={transfer?.fromLocation?.name ?? ''}
          toLocation={transfer?.toLocation?.name ?? ''}
          issuedBy={personName(transfer?.sentBy) ?? ''}
          receivedByName={personName(transfer?.receivedBy) ?? ''}
          rows={rows}
        />
      </div>
    </>
  );

  return { node, print: () => window.print(), download };
};
